// Super-admin rank-and-rent dashboard. Create agency-owned sites + assign
// leads to whichever sub-account is currently renting that site. Re-assign
// at any time — new leads route instantly.
import React, { useEffect, useState } from 'react';
import { G, styles, api } from '../theme.js';
import { Btn, Badge, Field, Input, Select, Modal, Spinner } from '../ui.jsx';
import { WebsiteBuilder } from './WebsiteBuilder.jsx';

const INDUSTRIES = [
  { id:'hvac',label:'HVAC' },{ id:'plumbing',label:'Plumbing' },{ id:'electrical',label:'Electrical' },
  { id:'roofing',label:'Roofing' },{ id:'garage',label:'Garage Doors' },{ id:'handyman',label:'Handyman' },
  { id:'water_damage',label:'Water Damage Restoration' },{ id:'mold',label:'Mold Remediation' },
  { id:'emergency_plumbing',label:'24hr Emergency Plumbing' },{ id:'emergency_hvac',label:'24hr Emergency HVAC' },
  { id:'storm_damage',label:'Storm Damage + Tree Removal' },{ id:'sewage_cleanup',label:'Sewage Cleanup' },
  { id:'foundation',label:'Foundation Repair' },{ id:'generator',label:'Home Generator' },
  { id:'glass_emergency',label:'Emergency Glass / Board-Up' },{ id:'septic',label:'Emergency Septic' },
];

// Curated high-ticket + high-urgency + tertiary-city targets. These niches
// drive call-first behavior (visitor is panicking → immediate tel: tap). The
// cities are 50k-250k population where local SEO has less competition than
// primary metros. Every entry uses the emergency-tuned site templates.
const CURATED_R_AND_R_TARGETS = [
  // Florida — storm exposed + retiree demographics
  { name:'Zephyrhills Emergency Water Damage Pros',   industry:'water_damage',       location:'Zephyrhills, FL' },
  { name:'Palm Coast Emergency Roof Tarp & Repair',   industry:'storm_damage',       location:'Palm Coast, FL' },
  { name:'Dade City Sewage Cleanup Specialists',      industry:'sewage_cleanup',     location:'Dade City, FL' },
  { name:'Port Charlotte Mold Remediation Experts',   industry:'mold',               location:'Port Charlotte, FL' },
  { name:'Bradenton 24hr Emergency HVAC',             industry:'emergency_hvac',     location:'Bradenton, FL' },
  { name:'Lakeland Emergency HVAC Response',          industry:'emergency_hvac',     location:'Lakeland, FL' },
  { name:'Panama City Water Damage Restoration',      industry:'water_damage',       location:'Panama City, FL' },
  { name:'Ocala Emergency Septic Pump-Out',           industry:'septic',             location:'Ocala, FL' },
  { name:'Palm Bay Whole-Home Generator Pros',        industry:'generator',          location:'Palm Bay, FL' },
  // Georgia — inland storm exposure + growing tertiary markets
  { name:'Athens 24hr Emergency Plumbers',            industry:'emergency_plumbing', location:'Athens, GA' },
  { name:'Warner Robins Emergency Plumbing',          industry:'emergency_plumbing', location:'Warner Robins, GA' },
  { name:'Rome GA Storm Damage & Tree Removal',       industry:'storm_damage',       location:'Rome, GA' },
  { name:'Macon Emergency Glass & Board-Up',          industry:'glass_emergency',    location:'Macon, GA' },
  // Carolinas — hurricane path + humid mold pressure
  { name:'Wilmington NC Mold Remediation',            industry:'mold',               location:'Wilmington, NC' },
  { name:'Fayetteville Storm Damage Response',        industry:'storm_damage',       location:'Fayetteville, NC' },
  { name:'Rock Hill Water Damage Restoration',        industry:'water_damage',       location:'Rock Hill, SC' },
  // Texas — expansive clay + growth
  { name:'Waco Foundation Repair Experts',            industry:'foundation',         location:'Waco, TX' },
  { name:'Killeen Foundation Repair Specialists',     industry:'foundation',         location:'Killeen, TX' },
  // Tennessee — river flood exposure + growth
  { name:'Chattanooga Sewage Backup Cleanup',         industry:'sewage_cleanup',     location:'Chattanooga, TN' },
  { name:'Murfreesboro 24hr Emergency Plumbing',      industry:'emergency_plumbing', location:'Murfreesboro, TN' },
];

export function RankAndRent({ toast }) {
  const [sites, setSites] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [creating, setCreating] = useState(false);
  const [editingSiteId, setEditingSiteId] = useState(null);
  const [form, setForm] = useState({ name:'', industry:'hvac', location:'' });
  const [bulkOpen, setBulkOpen] = useState(false);
  const [citationsForSite, setCitationsForSite] = useState(null);
  const [wipeOpen, setWipeOpen] = useState(false);

  // Called from BulkLaunchModal to trigger the actual create
  const bulkCreate = async ({ phone, targets, autoProvisionNumbers }) => {
    return api('/api/sites/rank-and-rent/bulk', { method:'POST', body:{
      defaults: { phone, autoProvisionNumbers: !!autoProvisionNumbers },
      sites: targets,
    }});
  };

  const load = () => Promise.all([
    api('/api/sites/rank-and-rent').catch(()=>[]),
    api('/api/agency/accounts').catch(()=>[]),
  ]).then(([s, a]) => { setSites(s); setAccounts(a); });
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name.trim()) return toast('Name required', 'error');
    try { await api('/api/sites/rank-and-rent', { method:'POST', body: form }); setCreating(false); setForm({ name:'', industry:'hvac', location:'' }); load(); toast('✓ Created'); }
    catch (e) { toast(e.message, 'error'); }
  };
  // targetKey semantics: '' = no one (owner gets leads),
  //                     'MARKETPLACE' = route through lead distribution engine,
  //                     '<accountId>' = direct assign to that sub-account
  const assign = async (siteId, targetKey) => {
    const body = targetKey === 'MARKETPLACE'
      ? { routeToMarketplace: true, assignedAccountId: null }
      : { routeToMarketplace: false, assignedAccountId: targetKey || null };
    try {
      await api(`/api/sites/rank-and-rent/${siteId}/assign`, { method:'POST', body });
      load();
      toast(targetKey === 'MARKETPLACE' ? '✓ Routing through Lead Marketplace' : targetKey ? '✓ Assigned' : '✓ Unassigned');
    } catch (e) { toast(e.message, 'error'); }
  };
  const del = async (s) => {
    if (!confirm(`Permanently delete "${s.name}"? Site + pages + blog posts all gone.`)) return;
    try { await api(`/api/sites/rank-and-rent/${s.id}`, { method:'DELETE' }); load(); toast('Deleted'); }
    catch (e) { toast(e.message, 'error'); }
  };

  if (!sites) return <Spinner sz={32}/>;

  // Edit mode — render WebsiteBuilder scoped to the selected rank-and-rent site
  if (editingSiteId) {
    return <div>
      <div style={{ padding:'14px 24px', borderBottom:`1px solid ${G.cardBorder}`, display:'flex', alignItems:'center', gap:14 }}>
        <Btn sz="sm" onClick={()=>setEditingSiteId(null)}>← Back to Rank-and-Rent</Btn>
        <span style={{ fontSize:13, color:G.textMuted }}>Editing rank-and-rent site</span>
      </div>
      <WebsiteBuilder toast={toast} siteId={editingSiteId}/>
    </div>;
  }

  return <div style={{ padding:24, maxWidth:1100 }}>
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 }}>
      <div>
        <h1 style={{ margin:0, fontSize:24, fontWeight:800 }}>🏷 Rank-and-Rent Sites</h1>
        <div style={{ fontSize:13, color:G.textMuted, marginTop:4, lineHeight:1.5 }}>
          Sites you own. Each one ranks on its own. Assign a sub-account to receive its leads. Re-assign whenever you want — leads switch instantly.
        </div>
      </div>
      <div style={{ display:'flex', gap:8 }}>
        <Btn onClick={async ()=>{
          try {
            const d = await api('/api/sites/rank-and-rent-diagnose');
            const plain = d.rows.filter(r => !r.is_rank_and_rent);
            const summary = `📊 Site Diagnostic

Total sites in DB: ${d.totalSites}
Flagged as R&R: ${d.rrSites}
Not flagged as R&R: ${plain.length}

Your accountId: ${d.sessionAccountId}
Ghost accountId: ${d.ghostAccountId || '—'}

Recent sites:
${d.rows.slice(0, 15).map(r => `  ${r.is_rank_and_rent?'🏷 R&R':'  Site'} · ${r.name} (${r.slug}) · owner=${r.owner_account_id||'—'}`).join('\n')}

${plain.length ? "\nSome sites aren't tagged as R&R. Promote them?" : ''}`;
            if (plain.length && confirm(summary + '\n\nOK to promote all non-R&R sites to R&R?')) {
              for (const p of plain) {
                try { await api(`/api/sites/rank-and-rent-promote/${p.id}`, { method:'POST' }); } catch {}
              }
              toast(`✓ Promoted ${plain.length} site${plain.length===1?'':'s'}`);
              load();
            } else {
              alert(summary);
            }
          } catch (e) { toast(e.message, 'error'); }
        }}>🔍 Diagnose</Btn>
        <Btn onClick={async ()=>{
          try {
            const r = await api('/api/sites/rank-and-rent/backfill-auto-seo', { method:'POST' });
            toast(`✓ Turned on auto-SEO for ${r.flipped}/${r.sitesScanned} site${r.sitesScanned===1?'':'s'}`);
            load();
          } catch (e) { toast(e.message, 'error'); }
        }}>⚡ Turn on Auto-SEO (all)</Btn>
        <Btn v="cyan" onClick={()=>setBulkOpen(true)}>🚀 Bulk Launch Curated Sites</Btn>
        <Btn v="danger" onClick={()=>setWipeOpen(true)}>🗑 Wipe All R&R Sites</Btn>
        <Btn onClick={async()=>{
          if (!confirm('Fire 6 blog posts on EVERY R&R site right now? This burns Claude tokens (~$1 for 20 sites × 6 posts) but jumps rank timeline from ~30 days to ~10-14 days.')) return;
          toast('🚀 Burst starting — this runs 60-120 seconds in the background');
          try {
            const r = await api('/api/sites/rank-and-rent/burst-network', { method:'POST', body:{ postsPerSite: 6 } });
            toast(`✓ Content burst: ${r.totalGenerated} posts across ${r.sites} sites`);
            load();
          } catch (e) { toast(e.message, 'error'); }
        }}>🚀 Boost Content (all sites)</Btn>
        <Btn onClick={async()=>{
          try {
            const h = await api('/api/sites/rank-and-rent/system-health');
            const readyToEarn = h.infrastructure.marketplaceActiveBuyers > 0 && h.infrastructure.rrSitesPublished > 0 && h.integrations.twilio && h.integrations.stripe;
            alert(
              `🩺 SYSTEM HEALTH · ${new Date(h.now).toLocaleTimeString()}\n\n` +
              `INFRASTRUCTURE\n` +
              `  R&R sites: ${h.infrastructure.rrSitesPublished}/${h.infrastructure.rrSitesTotal} published\n` +
              `  With tracking #: ${h.infrastructure.rrSitesWithTrackingNumber}\n` +
              `  Active marketplace buyers: ${h.infrastructure.marketplaceActiveBuyers}\n\n` +
              `LAST 7 DAYS\n` +
              `  Blog posts written: ${h.activity.blogPostsLast7d} (${h.activity.blogPostsLast24h} in last 24h)\n` +
              `  Syndicated backlinks: ${h.activity.backlinksSyndicatedLast7d}\n` +
              `  R&R tracking calls received: ${h.activity.rrTrackingCallsLast7d}\n` +
              `  Marketplace leads routed: ${h.activity.marketplaceLeadsLast7d}\n` +
              `  Marketplace revenue (30d): $${(h.activity.marketplaceRevenueLast30dCents/100).toFixed(2)}\n\n` +
              `INTEGRATIONS\n` +
              `  ${h.integrations.twilio?'✅':'❌'} Twilio (calls)\n` +
              `  ${h.integrations.stripe?'✅':'❌'} Stripe (billing)\n` +
              `  ${h.integrations.anthropic?'✅':'❌'} Anthropic (blog engine)\n` +
              `  ${h.integrations.sendgrid?'✅':'❌'} SendGrid (email)\n` +
              `  ${h.integrations.googleSearchConsole?'✅':'❌'} Google Search Console\n` +
              `  ${h.integrations.bingWebmaster?'✅':'❌'} Bing Webmaster\n` +
              `  ${h.integrations.devto?'✅':'❌'} Dev.to (syndication)\n` +
              `  ${h.integrations.hashnode?'✅':'❌'} Hashnode (syndication)\n` +
              `  ${h.integrations.blogger?'✅':'❌'} Blogger (syndication)\n\n` +
              (readyToEarn ? '💰 REVENUE-READY: infrastructure is live end-to-end.' : '⚠️ NOT YET REVENUE-READY. Missing:\n' +
                (h.infrastructure.marketplaceActiveBuyers===0?'  - Enroll at least 1 marketplace buyer\n':'') +
                (h.infrastructure.rrSitesPublished===0?'  - Publish at least 1 R&R site\n':'') +
                (!h.integrations.twilio?'  - Configure Twilio\n':'') +
                (!h.integrations.stripe?'  - Configure Stripe\n':'')
              )
            );
          } catch (e) { toast(e.message, 'error'); }
        }}>🩺 System Health</Btn>
        <Btn v="primary" onClick={()=>setCreating(true)}>+ New Site</Btn>
      </div>
    </div>

    {sites.length === 0 && !creating && <div style={{ ...styles.card, padding:48, textAlign:'center', color:G.textMuted }}>
      No rank-and-rent sites yet. Click <strong>+ New Site</strong> to build one.
    </div>}

    {creating && (
      <div style={{ ...styles.card, padding:18, marginBottom:14, borderColor:G.purple+'66' }}>
        <h3 style={{ margin:'0 0 10px', fontSize:15 }}>New Rank-and-Rent Site</h3>
        <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr 1fr auto auto', gap:10, alignItems:'flex-end' }}>
          <Field label="Site name"><Input value={form.name} onChange={e=>setForm({...form, name:e.target.value})} placeholder="Tampa Plumbers Pro"/></Field>
          <Field label="Industry">
            <Select value={form.industry} onChange={e=>setForm({...form, industry:e.target.value})}>
              {INDUSTRIES.map(i => <option key={i.id} value={i.id}>{i.label}</option>)}
            </Select>
          </Field>
          <Field label="Location"><Input value={form.location} onChange={e=>setForm({...form, location:e.target.value})} placeholder="Tampa, FL"/></Field>
          <Btn onClick={()=>setCreating(false)}>Cancel</Btn>
          <Btn v="primary" onClick={create}>Create</Btn>
        </div>
      </div>
    )}

    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(360px, 1fr))', gap:14 }}>
      {sites.map(s => (
        <div key={s.id} style={{ ...styles.card, padding:18 }}>
          <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:10 }}>
            <div style={{ flex:1, minWidth:0 }}>
              <strong style={{ fontSize:15 }}>{s.name}</strong>
              <div style={{ fontSize:12, color:G.textMuted, marginTop:2 }}>{s.industry} · {s.location || '—'}</div>
              <a href={s.publicUrl} target="_blank" rel="noreferrer" style={{ fontSize:12, color:G.cyan, textDecoration:'none' }}>{s.publicUrl}</a>
            </div>
            <div style={{ display:'flex', gap:6, flexWrap:'wrap', justifyContent:'flex-end' }}>
              <Badge c={s.published?G.green:G.yellow} bg={(s.published?G.green:G.yellow)+'22'}>{s.published?'Live':'Draft'}</Badge>
              {s.blogEnabled && <Badge c={G.cyan} bg={G.cyan+'22'} title={`Auto-blog ${s.blogCadence||'weekly'} · ${s.postsPublished||0} posts published · next ${s.blogNextRunAt ? new Date(s.blogNextRunAt).toLocaleDateString() : '?'}`}>⚡ {s.postsPublished||0} posts</Badge>}
              {s.syndicated7d > 0 && <Badge c={G.purple} bg={G.purpleDim} title="External backlinks generated last 7 days">🔗 {s.syndicated7d}/7d</Badge>}
            </div>
          </div>

          <TrackingNumberBlock site={s} toast={toast} onProvisioned={load}/>
          <PhoneField site={s} toast={toast} onSaved={load}/>

          <Field label="Lead routing">
            <Select
              value={s.routeToMarketplace ? 'MARKETPLACE' : (s.assignedAccountId || '')}
              onChange={e=>assign(s.id, e.target.value)}
            >
              <option value="">— No one (you receive leads) —</option>
              <option value="MARKETPLACE">🔀 Lead Marketplace (auto-distribute)</option>
              <optgroup label="Assign to sub-account">
                {accounts.filter(a => a.status === 'active').map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
              </optgroup>
            </Select>
          </Field>
          {s.routeToMarketplace && (
            <div style={{ fontSize:11, color:G.cyan, marginTop:6 }}>
              Leads route through the Marketplace: best-fit buyer wins by vertical + zip + bid.
            </div>
          )}

          <SiteStatsRow siteId={s.id}/>

          <div style={{ display:'flex', gap:6, marginTop:10, flexWrap:'wrap' }}>
            <Btn sz="sm" v="primary" onClick={()=>setEditingSiteId(s.id)}>✏️ Edit</Btn>
            <Btn sz="sm" onClick={()=>window.open(s.publicUrl, '_blank')}>🌐 Preview</Btn>
            <Btn sz="sm" v="cyan" onClick={()=>setCitationsForSite(s)}>🔗 Backlinks</Btn>
            <Btn sz="sm" v="danger" onClick={()=>del(s)}>🗑</Btn>
          </div>
        </div>
      ))}
    </div>

    {bulkOpen && <BulkLaunchModal onClose={()=>setBulkOpen(false)} onCreated={()=>{ setBulkOpen(false); load(); }} toast={toast}/>}
    {citationsForSite && <CitationsModal site={citationsForSite} onClose={()=>setCitationsForSite(null)} toast={toast}/>}
    {wipeOpen && <WipeAllModal sites={sites} onClose={()=>setWipeOpen(false)} onWiped={()=>{ setWipeOpen(false); load(); }} toast={toast}/>}
  </div>;
}

function WipeAllModal({ sites, onClose, onWiped, toast }) {
  const [checked, setChecked] = React.useState(false);
  const [wiping, setWiping] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [diag, setDiag] = React.useState(null);
  const [error, setError] = React.useState(null);
  const count = (sites || []).length;
  const runDiagnose = async () => {
    try { const d = await api('/api/sites/rank-and-rent/wipe-diagnose'); setDiag(d); }
    catch (e) { setError('Diagnose: ' + e.message); }
  };
  const doWipe = async () => {
    setWiping(true); setError(null);
    try {
      const r = await api('/api/sites/rank-and-rent/wipe-all', { method:'POST', body:{ confirm:'DELETE_ALL_R_AND_R' }});
      setResult(r);
      if (r.remaining === 0) toast(`✓ All ${r.deleted} R&R sites deleted`);
      else toast(`⚠ Deleted ${r.deleted}, ${r.remaining} survived`, 'error');
    } catch (e) { setError('Wipe: ' + e.message); toast(e.message, 'error'); }
    finally { setWiping(false); }
  };
  const overlay = { position:'fixed', inset:0, background:'rgba(0,0,0,.65)', zIndex:9999, display:'flex', alignItems:'flex-start', justifyContent:'center', overflow:'auto', padding:'40px 16px' };
  const card = { background:G.card, borderRadius:14, border:`1px solid ${G.border}`, width:'100%', maxWidth:600, padding:'24px 26px' };
  return <div style={overlay} onClick={onClose}>
    <div style={card} onClick={e=>e.stopPropagation()}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:14 }}>
        <div>
          <div style={{ fontSize:20, fontWeight:800, color:G.red }}>🗑 Delete All R&R Sites</div>
          <div style={{ fontSize:13, color:G.textMuted, marginTop:4 }}>Hard-delete every R&R site and release every Twilio tracking number.</div>
        </div>
        <button onClick={onClose} style={{ background:'none', border:'none', color:G.textMuted, fontSize:22, cursor:'pointer' }}>×</button>
      </div>

      {!result && <>
        <div style={{ ...styles.card, background:G.red+'11', border:`1px solid ${G.red}44`, padding:14, marginBottom:14 }}>
          <div style={{ fontSize:14, fontWeight:700, marginBottom:6 }}>This deletes:</div>
          <ul style={{ margin:0, paddingLeft:20, fontSize:13, color:G.textMuted, lineHeight:1.7 }}>
            <li><b>{count} R&R site{count===1?'':'s'}</b> and all their pages + blog posts</li>
            <li>SEO history, backlink citations, call logs, conversion variants</li>
            <li>Every Twilio tracking number gets released (stops the $1.15/mo per number)</li>
          </ul>
          <div style={{ fontSize:13, color:G.red, marginTop:10, fontWeight:700 }}>Irreversible.</div>
        </div>

        <label style={{ display:'flex', alignItems:'center', gap:10, padding:'14px 16px', borderRadius:10, background: checked ? G.red+'22' : G.input, border:`1px solid ${checked?G.red:G.border}`, cursor:'pointer', marginBottom:14 }}>
          <input type="checkbox" checked={checked} onChange={e=>setChecked(e.target.checked)} style={{ transform:'scale(1.3)' }}/>
          <span style={{ fontSize:14, fontWeight:600 }}>Yes, delete all {count} R&R sites permanently</span>
        </label>

        <div style={{ display:'flex', gap:8, justifyContent:'space-between', alignItems:'center' }}>
          <Btn sz="sm" onClick={runDiagnose}>🔍 Diagnose (dry-run)</Btn>
          <div style={{ display:'flex', gap:8 }}>
            <Btn onClick={onClose}>Cancel</Btn>
            <Btn v="danger" onClick={doWipe} disabled={!checked || wiping}>{wiping ? 'Deleting…' : `🗑 Delete ${count} sites`}</Btn>
          </div>
        </div>
        {diag && <pre style={{ marginTop:14, padding:12, background:G.input, borderRadius:6, fontSize:11, color:G.text, overflow:'auto', maxHeight:240 }}>{JSON.stringify(diag, null, 2)}</pre>}
        {error && <div style={{ marginTop:10, padding:10, background:G.red+'22', color:G.red, borderRadius:6, fontSize:12 }}>{error}</div>}
      </>}

      {result && <div>
        {result.remaining === 0 ? (
          <div style={{ ...styles.card, background:G.green+'11', border:`1px solid ${G.green}44`, padding:14, marginBottom:12 }}>
            <div style={{ fontSize:15, fontWeight:800, color:G.green }}>✅ Deleted all {result.deleted} R&R sites</div>
            <div style={{ fontSize:12, color:G.textMuted, marginTop:4 }}>Twilio tracking numbers released in the background.</div>
          </div>
        ) : (
          <div style={{ ...styles.card, background:G.red+'11', border:`1px solid ${G.red}44`, padding:14, marginBottom:12 }}>
            <div style={{ fontSize:15, fontWeight:800, color:G.red }}>⚠ {result.deleted} deleted · {result.remaining} survived</div>
            <div style={{ fontSize:12, color:G.textMuted, marginTop:4 }}>See which failed and why below.</div>
          </div>
        )}
        <div style={{ maxHeight:300, overflowY:'auto', border:`1px solid ${G.border}`, borderRadius:8, padding:10 }}>
          {(result.perSite||[]).map(s => (
            <div key={s.id} style={{ padding:'6px 8px', fontSize:12, borderBottom:`1px solid ${G.border}`, display:'flex', justifyContent:'space-between' }}>
              <span>{s.siteRowDeleted ? '✓' : '✗'} {s.slug}</span>
              <span style={{ color:G.textMuted }}>{s.error || (s.siteRowDeleted ? 'deleted' : 'row survived')}</span>
            </div>
          ))}
        </div>
        <details style={{ marginTop:10 }}>
          <summary style={{ cursor:'pointer', fontSize:11, color:G.cyan }}>Raw response (screenshot for support)</summary>
          <pre style={{ padding:12, background:G.input, borderRadius:6, fontSize:10, color:G.text, overflow:'auto', maxHeight:200 }}>{JSON.stringify(result, null, 2)}</pre>
        </details>
        <div style={{ display:'flex', justifyContent:'flex-end', marginTop:14 }}>
          <Btn v="primary" onClick={onWiped}>Done</Btn>
        </div>
      </div>}
    </div>
  </div>;
}

// Free backlink checklist modal — copy-paste NAP + click-through directory URLs
// + per-directory status tracking so no site slips through.
function CitationsModal({ site, onClose, toast }) {
  const [data, setData] = React.useState(null);
  const load = () => api(`/api/sites/rank-and-rent/${site.id}/citations`).then(setData).catch(e=>toast(e.message,'error'));
  React.useEffect(() => { load(); }, [site.id]);
  const setStatus = async (dirId, status) => {
    try { await api(`/api/sites/rank-and-rent/${site.id}/citations/${dirId}`, { method:'PATCH', body:{ status }}); load(); }
    catch (e) { toast(e.message, 'error'); }
  };
  const copyNap = () => {
    if (!data?.nap) return;
    const n = data.nap;
    const text = `Business Name: ${n.businessName}
Category: ${n.industry}
Phone: ${n.phone}
Website: ${n.website}
City: ${n.city}
State: ${n.state}
Service Areas: ${(n.serviceAreas||[]).join(', ')}
Hours: ${n.hoursOfOperation}
Description: ${n.description}
Keywords: ${(n.keywords||[]).join(', ')}`;
    navigator.clipboard.writeText(text).then(() => toast('✓ NAP copied — paste into any directory form'));
  };
  const overlay = { position:'fixed', inset:0, background:'rgba(0,0,0,.65)', zIndex:9999, display:'flex', alignItems:'flex-start', justifyContent:'center', overflow:'auto', padding:'32px 16px' };
  const card = { background:G.card, borderRadius:14, border:`1px solid ${G.border}`, width:'100%', maxWidth:820, padding:'24px 26px', boxShadow:'0 20px 60px rgba(0,0,0,.5)' };

  if (!data) return <div style={overlay} onClick={onClose}><div style={card} onClick={e=>e.stopPropagation()}><Spinner sz={28}/></div></div>;

  const done = data.items.filter(i => i.status !== 'todo').length;
  const groups = {
    Highest: data.items.filter(i => i.impact.startsWith('Highest')),
    High:    data.items.filter(i => i.impact.startsWith('High ') || i.impact.startsWith('High-')),
    Medium:  data.items.filter(i => i.impact.startsWith('Medium')),
    Low:     data.items.filter(i => i.impact.startsWith('Low')),
    Other:   data.items.filter(i => !['Highest','High ','High-','Medium','Low'].some(p => i.impact.startsWith(p))),
  };
  return <div style={overlay} onClick={onClose}>
    <div style={card} onClick={e=>e.stopPropagation()}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:12 }}>
        <div>
          <div style={{ fontSize:20, fontWeight:800 }}>🔗 Free Backlink Sources</div>
          <div style={{ fontSize:12, color:G.textMuted, marginTop:4 }}>{site.name} · {done}/{data.items.length} submitted</div>
        </div>
        <button onClick={onClose} style={{ background:'none', border:'none', color:G.textMuted, fontSize:22, cursor:'pointer' }}>×</button>
      </div>

      {/* NAP block */}
      <div style={{ ...styles.card, padding:12, background:G.input, marginBottom:14 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
          <div style={{ fontSize:11, color:G.textMuted, textTransform:'uppercase', fontWeight:700 }}>Copy-paste NAP for every directory</div>
          <Btn sz="sm" v="primary" onClick={copyNap}>📋 Copy NAP</Btn>
        </div>
        <div style={{ fontSize:12, lineHeight:1.6, color:G.text }}>
          <div><strong>{data.nap.businessName}</strong></div>
          <div>{data.nap.industry} · {data.nap.city}, {data.nap.state}</div>
          <div>📞 {data.nap.phone || '—'} · 🌐 {data.nap.website}</div>
        </div>
      </div>

      {/* Directory groups */}
      {Object.entries(groups).map(([groupName, items]) => items.length > 0 && (
        <div key={groupName} style={{ marginBottom:14 }}>
          <div style={{ fontSize:11, color:G.textMuted, textTransform:'uppercase', fontWeight:700, marginBottom:6 }}>{groupName} Impact</div>
          <div style={{ border:`1px solid ${G.border}`, borderRadius:8, overflow:'hidden' }}>
            {items.map(d => (
              <div key={d.id} style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 12px', borderTop:`1px solid ${G.border}`, background: d.status !== 'todo' ? G.green+'11' : 'transparent' }}>
                <input type="checkbox" checked={d.status !== 'todo'} onChange={e=>setStatus(d.id, e.target.checked ? 'submitted' : 'todo')}/>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:13, fontWeight:600 }}>
                    <a href={d.url} target="_blank" rel="noreferrer" style={{ color:G.text, textDecoration:'none' }}>{d.name} →</a>
                    {d.requiresVerification && <span style={{ marginLeft:6, fontSize:10, background:G.yellow+'22', color:G.yellow, padding:'2px 6px', borderRadius:4 }}>needs verify</span>}
                  </div>
                  <div style={{ fontSize:11, color:G.textMuted, marginTop:2 }}>{d.impact}{d.note ? ` — ${d.note}` : ''}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      <div style={{ fontSize:11, color:G.textMuted, marginTop:10, padding:10, background:G.input, borderRadius:6 }}>
        <strong>Free network cross-link is already automatic.</strong> Every AI-generated blog post on this site auto-embeds 1-2 contextual links to related R&R sites in your network → real backlinks passed around every week, zero effort.
      </div>
    </div>
  </div>;
}

// Bulk-launch modal — properly-sized dialog for the 20-site curated campaign.
// Native window.prompt truncates its text on mobile, so we render a full modal
// with a scrollable target list + phone input + explicit Launch button.
function BulkLaunchModal({ onClose, onCreated, toast }) {
  const [phone, setPhone] = React.useState('');
  const [autoProvision, setAutoProvision] = React.useState(true);
  const [launching, setLaunching] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [selected, setSelected] = React.useState(() => new Set(CURATED_R_AND_R_TARGETS.map((_,i)=>i)));

  const toggle = (i) => {
    const next = new Set(selected);
    next.has(i) ? next.delete(i) : next.add(i);
    setSelected(next);
  };

  const launch = async () => {
    if (!phone.trim()) return toast('Enter a click-to-call phone number', 'error');
    if (!selected.size) return toast('Pick at least one site', 'error');
    setLaunching(true);
    try {
      const sites = CURATED_R_AND_R_TARGETS.filter((_,i)=>selected.has(i));
      const r = await api('/api/sites/rank-and-rent/bulk', { method:'POST', body:{
        defaults: { phone: phone.trim(), autoProvisionNumbers: autoProvision },
        sites,
      }});
      setResult(r);
      toast(`✓ Created ${r.createdCount} sites${r.errorCount?` · ${r.errorCount} errors`:''}`);
    } catch (e) { toast(e.message, 'error'); }
    finally { setLaunching(false); }
  };

  const overlay = {
    position:'fixed', inset:0, background:'rgba(0,0,0,.65)', zIndex:9999,
    display:'flex', alignItems:'flex-start', justifyContent:'center', overflow:'auto', padding:'40px 16px',
  };
  const card = {
    background:G.card, borderRadius:14, border:`1px solid ${G.border}`,
    width:'100%', maxWidth:640, padding:'24px 26px', boxShadow:'0 20px 60px rgba(0,0,0,.5)',
  };

  return <div style={overlay} onClick={onClose}>
    <div style={card} onClick={e=>e.stopPropagation()}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:12, gap:14 }}>
        <div>
          <div style={{ fontSize:20, fontWeight:800 }}>🚀 Launch Rank-and-Rent Campaign</div>
          <div style={{ fontSize:12, color:G.textMuted, marginTop:4 }}>Bulk-creates {CURATED_R_AND_R_TARGETS.length} emergency + high-ticket sites across tertiary US cities.</div>
        </div>
        <button onClick={onClose} style={{ background:'none', border:'none', color:G.textMuted, fontSize:22, cursor:'pointer', lineHeight:1 }}>×</button>
      </div>

      {!result && <>
        <div style={{ background:G.input, padding:12, borderRadius:8, marginBottom:12, fontSize:12, color:G.textMuted, lineHeight:1.5 }}>
          <strong style={{ color:G.text }}>Each site launches with:</strong>
          <div>• Emergency-tuned template (call-first hero + urgency banner)</div>
          <div>• Auto-blog on weekly cadence</div>
          <div>• Auto-SEO refresh + IndexNow ping</div>
          <div>• Click-to-call button on every page</div>
        </div>

        <div style={{ marginBottom:12 }}>
          <div style={{ fontSize:12, fontWeight:700, color:G.textMuted, textTransform:'uppercase', marginBottom:6 }}>Fallback click-to-call phone (used until each site's local tracking number is provisioned)</div>
          <input value={phone} onChange={e=>setPhone(e.target.value)} placeholder="+1 813 555 0123"
            style={{ ...styles.inp, width:'100%', fontSize:16, padding:'12px 14px' }}/>
        </div>

        <label style={{ display:'flex', alignItems:'center', gap:8, marginBottom:12, cursor:'pointer', padding:12, borderRadius:8, background: autoProvision ? G.green+'11' : G.input, border:`1px solid ${autoProvision?G.green:G.border}` }}>
          <input type="checkbox" checked={autoProvision} onChange={e=>setAutoProvision(e.target.checked)}/>
          <div>
            <div style={{ fontWeight:700, fontSize:13 }}>📞 Auto-provision a local tracking number for each site</div>
            <div style={{ fontSize:11, color:G.textMuted, marginTop:2 }}>Buys a Twilio number in the site's area code + wires call routing (costs ~$1.15/mo per number). Uncheck to just use the fallback phone above.</div>
          </div>
        </label>

        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:6 }}>
          <div style={{ fontSize:12, fontWeight:700, color:G.textMuted, textTransform:'uppercase' }}>Targets ({selected.size}/{CURATED_R_AND_R_TARGETS.length} selected)</div>
          <div style={{ display:'flex', gap:6 }}>
            <button onClick={()=>setSelected(new Set(CURATED_R_AND_R_TARGETS.map((_,i)=>i)))} style={{ background:'none', border:'none', color:G.cyan, cursor:'pointer', fontSize:12 }}>All</button>
            <button onClick={()=>setSelected(new Set())} style={{ background:'none', border:'none', color:G.textMuted, cursor:'pointer', fontSize:12 }}>None</button>
          </div>
        </div>
        <div style={{ maxHeight:260, overflowY:'auto', border:`1px solid ${G.border}`, borderRadius:8 }}>
          {CURATED_R_AND_R_TARGETS.map((t, i) => {
            const on = selected.has(i);
            return <label key={i} style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 12px', borderBottom:`1px solid ${G.border}`, cursor:'pointer', background: on ? G.input : 'transparent' }}>
              <input type="checkbox" checked={on} onChange={()=>toggle(i)}/>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:13, fontWeight:600 }}>{t.name}</div>
                <div style={{ fontSize:11, color:G.textMuted }}>{t.industry.replace(/_/g,' ')} · {t.location}</div>
              </div>
            </label>;
          })}
        </div>

        <div style={{ display:'flex', gap:8, justifyContent:'flex-end', marginTop:16 }}>
          <Btn onClick={onClose}>Cancel</Btn>
          <Btn v="primary" onClick={launch} disabled={launching}>{launching ? 'Launching…' : `🚀 Launch ${selected.size} Sites`}</Btn>
        </div>
      </>}

      {result && <div>
        <div style={{ background:G.green+'22', padding:14, borderRadius:8, marginBottom:14 }}>
          <div style={{ fontSize:15, fontWeight:800, color:G.green }}>✅ {result.createdCount} sites created and live</div>
          {result.errorCount > 0 && <div style={{ fontSize:12, color:G.red, marginTop:4 }}>{result.errorCount} errors — see below</div>}
        </div>

        <div style={{ fontSize:13, fontWeight:700, marginBottom:8 }}>Next steps to hit calls in 24h:</div>
        <ol style={{ fontSize:13, color:G.textMuted, lineHeight:1.7, paddingLeft:20, margin:0 }}>
          <li>Point custom domains at each site (or use <code>{location.origin}/site/&lt;slug&gt;</code>). Custom domains rank much faster.</li>
          <li>Turn on Google Search Ads on the top 5 highest-ticket ones ($20-50/day per site). This is the <strong>only</strong> way to get calls in &lt;24h.</li>
          <li>Submit each sitemap to Google Search Console + Bing Webmaster.</li>
          <li>Post the URLs to relevant Reddit / Nextdoor community threads for immediate crawl signal.</li>
        </ol>
        <div style={{ fontSize:12, color:G.textMuted, marginTop:12, padding:10, background:G.input, borderRadius:6 }}>
          Organic-only ranking to first calls: realistically <strong>5-30 days</strong> depending on niche competition.
        </div>

        <div style={{ maxHeight:200, overflowY:'auto', marginTop:14, border:`1px solid ${G.border}`, borderRadius:8 }}>
          {(result.created||[]).map((s,i)=>(
            <div key={s.id} style={{ padding:'6px 10px', borderBottom:`1px solid ${G.border}`, fontSize:12, display:'flex', justifyContent:'space-between' }}>
              <span>{s.name}</span>
              <a href={s.publicUrl} target="_blank" rel="noreferrer" style={{ color:G.cyan, textDecoration:'none' }}>Preview →</a>
            </div>
          ))}
        </div>

        <div style={{ display:'flex', justifyContent:'flex-end', marginTop:14 }}>
          <Btn v="primary" onClick={onCreated}>Done</Btn>
        </div>
      </div>}
    </div>
  </div>;
}

// R&R tracking number — a Twilio-provisioned local number that appears on the
// click-to-call button and routes inbound calls to the assigned sub-account
// or the ring group of eligible marketplace buyers. Auto-selects an area code
// based on the site's city.
function TrackingNumberBlock({ site, toast, onProvisioned }) {
  const [provisioning, setProvisioning] = React.useState(false);
  const provision = async () => {
    if (site.trackingPhone && !confirm(`Site already has a tracking number (${site.trackingPhone}). Provision another?`)) return;
    setProvisioning(true);
    try {
      const r = await api(`/api/sites/rank-and-rent/${site.id}/provision-tracking-number`, { method:'POST' });
      toast(r.alreadyProvisioned ? `Tracking number: ${r.phoneNumber}` : `✓ Provisioned ${r.phoneNumber}`);
      onProvisioned && onProvisioned();
    } catch (e) { toast(e.message, 'error'); }
    finally { setProvisioning(false); }
  };
  return <Field label="🏷 Tracking Number (local area code, routes calls)">
    <div style={{ display:'flex', gap:6, alignItems:'center' }}>
      {site.trackingPhone
        ? <div style={{ flex:1, padding:'8px 12px', borderRadius:6, background:G.green+'22', color:G.green, fontWeight:700, fontSize:14 }}>{site.trackingPhone}</div>
        : <div style={{ flex:1, padding:'8px 12px', borderRadius:6, background:G.input, color:G.textMuted, fontSize:13 }}>Not provisioned</div>}
      <Btn sz="sm" v={site.trackingPhone ? 'ghost' : 'primary'} onClick={provision} disabled={provisioning}>
        {provisioning ? '…' : site.trackingPhone ? 'Re-provision' : 'Provision'}
      </Btn>
    </div>
  </Field>;
}

// Site phone number — powers the click-to-call button rendered on every page
// of the public site. Empty falls back to the owning account's phone. Inline
// save so Kyle can update without leaving the R&R tab.
function PhoneField({ site, toast, onSaved }) {
  const [phone, setPhone] = React.useState(site.phone || '');
  const [dirty, setDirty] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api(`/api/sites/rank-and-rent/${site.id}/settings`, { method:'PATCH', body:{ phone: phone.trim() || null }});
      setDirty(false); toast('✓ Phone saved · click-to-call updated');
      onSaved && onSaved();
    } catch(e) { toast(e.message, 'error'); }
    finally { setSaving(false); }
  };
  return <Field label="📞 Click-to-Call Number (shown as sticky button)">
    <div style={{ display:'flex', gap:6 }}>
      <Input value={phone}
        onChange={e=>{ setPhone(e.target.value); setDirty(true); }}
        placeholder="+1 813 555 0123"/>
      {dirty && <Btn sz="sm" v="primary" onClick={save} disabled={saving}>{saving?'…':'Save'}</Btn>}
    </div>
  </Field>;
}

// Per-site stats strip — blog count, leads, calls, AI questions, SEO rank.
// Rank pulled from sites_seo_status (manual entry today; auto-pull from
// SerpAPI/DataForSEO next iteration). Empty/zero values still render so the
// owner sees "0" rather than nothing.
function SiteStatsRow({ siteId }) {
  const [stats, setStats] = React.useState(null);
  const [editing, setEditing] = React.useState(false);
  const [seo, setSeo] = React.useState({});
  const [refreshing, setRefreshing] = React.useState(false);
  const load = () => api(`/api/sites/rank-and-rent/${siteId}/stats`).then(setStats).catch(()=>{});
  React.useEffect(() => { load(); }, [siteId]);
  if (!stats) return null;
  const rank = (n) => n == null ? '—' : `#${n}`;
  const rankColor = (n) => n == null ? G.textMuted : n <= 3 ? G.green : n <= 10 ? G.yellow : G.red;
  const saveSeo = async () => {
    try {
      await api(`/api/sites/rank-and-rent/${siteId}/seo`, { method:'PATCH', body: seo });
      const fresh = await api(`/api/sites/rank-and-rent/${siteId}/stats`);
      setStats(fresh); setEditing(false);
    } catch(e) { alert(e.message); }
  };
  const refreshGsc = async () => {
    setRefreshing(true);
    try { await api(`/api/sites/rank-and-rent/${siteId}/refresh-gsc`, { method:'POST' }); await load(); }
    catch (e) { alert(e.message); }
    finally { setRefreshing(false); }
  };
  const gsc = stats.gsc || {};
  const posColor = (n) => n == null ? G.textMuted : n <= 3 ? G.green : n <= 10 ? G.yellow : n <= 20 ? G.orange : G.red;
  return <div style={{ marginTop:12, padding:'10px 12px', background:G.input, borderRadius:8 }}>
    {/* Real-world Google Search Console block */}
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8 }}>
      <div style={{ fontSize:10, color:G.textMuted, textTransform:'uppercase', letterSpacing:'.05em', fontWeight:700 }}>📊 Real Google Search Console (last 30d)</div>
      {gsc.connected ? (
        <button onClick={refreshGsc} disabled={refreshing} style={{ background:'none', border:'none', color:G.cyan, fontSize:10, cursor:'pointer' }}>{refreshing?'…':'↻ Refresh'}</button>
      ) : (
        <a href="/#integrations" style={{ color:G.yellow, fontSize:10, textDecoration:'none' }}>⚠ Connect GSC in Integrations</a>
      )}
    </div>
    <div style={{ display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:8, fontSize:11, marginBottom:10 }}>
      <Stat label="Impressions" value={gsc.impressions ?? '—'} color={G.cyan}/>
      <Stat label="Clicks" value={gsc.clicks ?? '—'} color={G.green}/>
      <Stat label="CTR" value={gsc.ctr != null ? `${(gsc.ctr*100).toFixed(1)}%` : '—'} color={G.purple}/>
      <Stat label="Avg Position" value={gsc.avgPosition != null ? `#${gsc.avgPosition}` : '—'} color={posColor(gsc.avgPosition)}/>
    </div>
    {gsc.topQueries?.length > 0 && <div style={{ marginBottom:10 }}>
      <div style={{ fontSize:10, color:G.textMuted, textTransform:'uppercase', fontWeight:700, marginBottom:4 }}>Top keywords ranking for</div>
      <div style={{ maxHeight:120, overflowY:'auto', background:G.card, borderRadius:6, padding:6 }}>
        {gsc.topQueries.slice(0, 8).map((q, i) => (
          <div key={i} style={{ display:'flex', justifyContent:'space-between', fontSize:11, padding:'3px 4px' }}>
            <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:'55%' }}>{q.query}</span>
            <span style={{ color:G.textMuted }}>#{q.avgPosition} · {q.impressions}imp · {q.clicks}clk</span>
          </div>
        ))}
      </div>
    </div>}
    {gsc.lastSynced && <div style={{ fontSize:9, color:G.textMuted, marginBottom:8 }}>GSC synced {new Date(gsc.lastSynced).toLocaleString()} · property: {gsc.property || '—'}</div>}

    {/* Bing Webmaster block */}
    <BingBlock siteId={siteId} bing={stats.bing || {}}/>

    <div style={{ display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:8, fontSize:11 }}>
      <Stat label="Backlinks" value={stats.backlinks}/>
      <Stat label="Blog posts" value={stats.blogCount} color={G.cyan}/>
      <Stat label="Pages" value={stats.pageCount} color={G.cyan}/>
      <Stat label="AI Search" value={rank(stats.aiSearchRank)} color={rankColor(stats.aiSearchRank)}/>
      <Stat label="Leads" value={stats.leads} color={G.green}/>
      <Stat label="Calls" value={stats.calls} color={G.green}/>
    </div>
    {stats.targetKeyword && <div style={{ fontSize:10, color:G.textMuted, marginTop:6 }}>Target: <strong>{stats.targetKeyword}</strong>{stats.lastChecked ? ` · checked ${new Date(stats.lastChecked).toLocaleDateString()}` : ''}</div>}
    <div style={{ display:'flex', justifyContent:'flex-end', marginTop:6 }}>
      <button onClick={()=>{ setEditing(true); setSeo({ googleRank:stats.googleRank, aiSearchRank:stats.aiSearchRank, backlinks:stats.backlinks, organicClicks:stats.organicClicks, targetKeyword:stats.targetKeyword || '' }); }} style={{ background:'none', border:'none', color:G.cyan, fontSize:11, cursor:'pointer' }}>✏️ Manual overrides</button>
    </div>
    {editing && <div style={{ marginTop:10, padding:10, background:G.card, borderRadius:6, border:`1px solid ${G.cardBorder}` }}>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
        <Field label="Google Rank"><Input type="number" value={seo.googleRank ?? ''} onChange={e=>setSeo({...seo, googleRank: e.target.value ? Number(e.target.value) : null})}/></Field>
        <Field label="AI Search Rank"><Input type="number" value={seo.aiSearchRank ?? ''} onChange={e=>setSeo({...seo, aiSearchRank: e.target.value ? Number(e.target.value) : null})}/></Field>
        <Field label="Backlinks"><Input type="number" value={seo.backlinks ?? 0} onChange={e=>setSeo({...seo, backlinks: Number(e.target.value)||0})}/></Field>
        <Field label="Clicks/mo"><Input type="number" value={seo.organicClicks ?? 0} onChange={e=>setSeo({...seo, organicClicks: Number(e.target.value)||0})}/></Field>
        <Field label="Target Keyword" style={{ gridColumn:'1/-1' }}><Input value={seo.targetKeyword||''} onChange={e=>setSeo({...seo, targetKeyword: e.target.value})} placeholder="e.g. plumber tampa"/></Field>
      </div>
      <div style={{ display:'flex', justifyContent:'flex-end', gap:8, marginTop:8 }}>
        <Btn sz="xs" v="ghost" onClick={()=>setEditing(false)}>Cancel</Btn>
        <Btn sz="xs" v="primary" onClick={saveSeo}>Save</Btn>
      </div>
    </div>}
  </div>;
}
// Bing Webmaster block — free source for backlinks + a second impressions/clicks
// stream (Bing powers DuckDuckGo + partially seeds Perplexity's index). One-time
// paste of the API key from bing.com/webmasters → Settings → API Access.
function BingBlock({ siteId, bing }) {
  const [refreshing, setRefreshing] = React.useState(false);
  const [connectOpen, setConnectOpen] = React.useState(false);
  const [apiKey, setApiKey] = React.useState('');

  const connect = async () => {
    if (!apiKey.trim()) return;
    try {
      await api('/api/sites/rank-and-rent/bing/connect', { method:'POST', body:{ apiKey: apiKey.trim() }});
      setConnectOpen(false); setApiKey('');
      window.location.reload();
    } catch (e) { alert(e.message); }
  };
  const refresh = async () => {
    setRefreshing(true);
    try { await api(`/api/sites/rank-and-rent/${siteId}/refresh-bing`, { method:'POST' }); window.location.reload(); }
    catch (e) { alert(e.message); }
    finally { setRefreshing(false); }
  };

  return <div style={{ marginTop:10, padding:8, background:G.card, borderRadius:6 }}>
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:6 }}>
      <div style={{ fontSize:10, color:G.textMuted, textTransform:'uppercase', letterSpacing:'.05em', fontWeight:700 }}>🔵 Bing Webmaster (backlinks + Bing/DuckDuckGo)</div>
      {bing.connected ? (
        <button onClick={refresh} disabled={refreshing} style={{ background:'none', border:'none', color:G.cyan, fontSize:10, cursor:'pointer' }}>{refreshing?'…':'↻ Refresh'}</button>
      ) : (
        <button onClick={()=>setConnectOpen(!connectOpen)} style={{ background:'none', border:'none', color:G.yellow, fontSize:10, cursor:'pointer' }}>⚠ Paste API key</button>
      )}
    </div>
    {connectOpen && !bing.connected && <div style={{ display:'flex', gap:6, marginBottom:6 }}>
      <input value={apiKey} onChange={e=>setApiKey(e.target.value)} placeholder="Bing Webmaster API key" style={{ ...styles.inp, flex:1, fontSize:12 }}/>
      <Btn sz="xs" v="primary" onClick={connect}>Save</Btn>
    </div>}
    <div style={{ display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:8, fontSize:11 }}>
      <Stat label="Backlinks" value={bing.backlinks ?? '—'} color={G.orange}/>
      <Stat label="Bing Impressions" value={bing.impressions ?? '—'} color={G.cyan}/>
      <Stat label="Bing Clicks" value={bing.clicks ?? '—'} color={G.green}/>
    </div>
    {bing.topBacklinks?.length > 0 && <details style={{ marginTop:6 }}>
      <summary style={{ fontSize:10, color:G.cyan, cursor:'pointer' }}>Show top {bing.topBacklinks.length} backlinks</summary>
      <div style={{ maxHeight:120, overflowY:'auto', marginTop:4, fontSize:10 }}>
        {bing.topBacklinks.map((b, i) => (
          <div key={i} style={{ padding:'3px 0', borderBottom:`1px solid ${G.border}`, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
            <a href={b.source} target="_blank" rel="noreferrer" style={{ color:G.cyan, textDecoration:'none' }}>{b.source}</a>
            {b.anchor && <span style={{ color:G.textMuted }}> · "{b.anchor}"</span>}
          </div>
        ))}
      </div>
    </details>}
    {bing.lastSynced && <div style={{ fontSize:9, color:G.textMuted, marginTop:4 }}>Synced {new Date(bing.lastSynced).toLocaleString()}</div>}
  </div>;
}

function Stat({ label, value, color }) {
  return <div style={{ textAlign:'center' }}>
    <div style={{ fontSize:9, color:G.textMuted, textTransform:'uppercase', letterSpacing:'.04em' }}>{label}</div>
    <div style={{ fontSize:15, fontWeight:800, color: color || G.text }}>{value}</div>
  </div>;
}
